import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {changePaymentPassword, verifyLoginPassword} from "../../../api/commonApi";
import {defaultAlert} from "../../../utils/alert";

type Step = 'login' | 'first' | 'second';

const ChangePaymentPassword = () => {
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>('login');
    const [loginInput, setLoginInput] = useState('');
    const [firstPassword, setFirstPassword] = useState('');
    const [password, setPassword] = useState('');
    const [numbers, setNumbers] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 0]);
    const [isLoading, setIsLoading] = useState(false);

    // ── 로그인 비밀번호 확인 ──────────────────────────────
    const handleLoginSubmit = async () => {
        if (!loginInput || isLoading) return;
        setIsLoading(true);
        try {
            const response = await verifyLoginPassword({ password: loginInput });
            if (response.data.data === true) {
                setLoginInput('');
                setStep('first');
            } else {
                defaultAlert('비밀번호가 일치하지 않습니다.');
                setLoginInput('');
            }
        } catch (error: any) {
            if (error.response?.status === 403) {
                defaultAlert('비밀번호가 일치하지 않습니다.');
            } else {
                defaultAlert('비밀번호 확인에 실패했습니다.');
            }
            setLoginInput('');
        } finally {
            setIsLoading(false);
        }
    };

    // ── 숫자판 ────────────────────────────────────────────
    const handleNumberClick = (num: number) => {
        if (password.length < 6 && !isLoading) {
            const newPassword = password + num;
            setPassword(newPassword);
            if (newPassword.length === 6) {
                if (step === 'first') handleFirstStep(newPassword);
                else handleSecondStep(newPassword);
            }
        }
    };

    const handleFirstStep = (pw: string) => {
        setFirstPassword(pw);
        setPassword('');
        setStep('second');
        setNumbers([1, 2, 3, 4, 5, 6, 7, 8, 9, 0]);
    };

    const handleSecondStep = async (pw: string) => {
        if (pw !== firstPassword) {
            defaultAlert('비밀번호가 일치하지 않습니다.\n다시 입력해 주세요.');
            setFirstPassword('');
            setPassword('');
            setStep('first');
            setNumbers([1, 2, 3, 4, 5, 6, 7, 8, 9, 0]);
            return;
        }
        setIsLoading(true);
        try {
            await changePaymentPassword({ paymentPassword: pw });
            defaultAlert('결제비밀번호가 변경되었습니다.');
            navigate(-1);
        } catch {
            defaultAlert('결제비밀번호 변경에 실패했습니다.');
            setFirstPassword('');
            setPassword('');
            setStep('first');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = () => { if (!isLoading) setPassword(prev => prev.slice(0, -1)); };
    const handleShuffle = () => { if (!isLoading) setNumbers(prev => [...prev].sort(() => Math.random() - 0.5)); };

    const handleBack = () => {
        if (step === 'second') { setStep('first'); setFirstPassword(''); setPassword(''); }
        else if (step === 'first') { setStep('login'); setPassword(''); }
        else navigate(-1);
    };

    const passwordIndicators = Array.from({ length: 6 }, (_, i) => i);

    return (
        <>
            <main className="container no-bottom-nav">
                {/* ── 로그인 비밀번호 입력 ── */}
                {step === 'login' && (
                    <>
                        <div className="bottom-btn">
                            <button
                                type="button"
                                className="btn confirm"
                                onClick={handleLoginSubmit}
                                disabled={isLoading || !loginInput}
                            >
                                {isLoading ? '확인 중...' : '확인'}
                            </button>
                        </div>

                        <section className="tit-box-wrap">
                            <p className="subtit">
                                현재 로그인 비밀번호가 일치하는 경우<br/>
                                결제비밀번호를 변경할 수 있습니다.
                            </p>
                        </section>

                        <form className="form-box">
                            <section className="bg-white">
                                <fieldset>
                                    <legend>로그인 비밀번호 확인 폼</legend>
                                    <div className="inner-box">
                                        <div className="form-item">
                                            <label className="input-tit">현재 비밀번호</label>
                                            <div className="input-box">
                                                <input
                                                    type="password"
                                                    value={loginInput}
                                                    onChange={e => setLoginInput(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && handleLoginSubmit()}
                                                    placeholder="현재 비밀번호를 입력하세요"
                                                    disabled={isLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </section>
                        </form>
                    </>
                )}

                {/* ── 결제비밀번호 숫자판 ── */}
                {(step === 'first' || step === 'second') && (
                    <>
                        <section>
                            <div className="passwordForm">
                                <h1>{step === 'first' ? '결제비밀번호 변경' : '결제비밀번호 확인'}</h1>
                                <h2>{step === 'first' ? '새로운 결제비밀번호 6자리를 입력해 주세요.' : '새로운 결제비밀번호를 한 번 더 입력해 주세요.'}</h2>
                                <div className="password-input inlinePassword-input">
                                    <div className="password-indicator">
                                        {passwordIndicators.map((i) => (
                                            <span key={i} className={`passCircle ${i < password.length ? 'filled' : ''}`} id={`passwd${i}`} />
                                        ))}
                                    </div>
                                </div>
                                {isLoading && <p className="loading-text">처리 중...</p>}
                            </div>
                        </section>

                        <section>
                            <div className="fixedBottom">
                                <ul className="keyboardF">
                                    {numbers.slice(0, 9).map((num) => (
                                        <li key={num}>
                                            <button type="button" className="key-btn" onClick={() => handleNumberClick(num)} disabled={isLoading}>
                                                {num}
                                            </button>
                                        </li>
                                    ))}
                                    <li>
                                        <button type="button" className="key-btn txt" onClick={handleShuffle} disabled={isLoading}>재배열</button>
                                    </li>
                                    <li>
                                        <button type="button" className="key-btn" onClick={() => handleNumberClick(numbers[9])} disabled={isLoading}>
                                            {numbers[9]}
                                        </button>
                                    </li>
                                    <li>
                                        <button type="button" className="key-btn txt" onClick={handleDelete} disabled={isLoading}>삭제</button>
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </>
                )}
            </main>
        </>
    );
};

export default ChangePaymentPassword;